import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { getUserFeedback, addFeedback } from '../../../firebase/services';
import { BottomNav } from '../../common/BottomNav';
import './MyFeedbackScreen.css';

const MyFeedbackScreen = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('submit'); // 'submit' or 'history'

  // Form state
  const [category, setCategory] = useState('General Feedback');
  const [feedbackText, setFeedbackText] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const feedback = await getUserFeedback(currentUser.uid);
        setFeedbackList(feedback);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching feedback:', err);
        setError('Failed to load your feedback');
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');

    if (!feedbackText.trim()) {
      setSubmitMessage('Feedback cannot be empty.');
      return;
    }

    setIsSubmitting(true);
    try {
      await addFeedback({ category, feedbackText });
      setSubmitMessage('Thank you! Your feedback has been submitted successfully.');
      setFeedbackText('');
      setCategory('General Feedback');

      // Refresh feedback list
      const feedback = await getUserFeedback(currentUser.uid);
      setFeedbackList(feedback);

      // Switch to history tab after 2 seconds
      setTimeout(() => {
        setActiveTab('history');
        setSubmitMessage('');
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitMessage('Failed to submit feedback. Please try again.');
    }
    setIsSubmitting(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return '#007bff';
      case 'in-progress':
        return '#ffc107';
      case 'resolved':
        return '#28a745';
      case 'wont-fix':
        return '#6c757d';
      default:
        return '#007bff';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'new':
        return 'New';
      case 'in-progress':
        return 'In Progress';
      case 'resolved':
        return 'Resolved';
      case 'wont-fix':
        return "Won't Fix";
      default:
        return 'New';
    }
  };

  if (loading) {
    return (
      <div className="my-feedback-screen">
        <header className="my-feedback-header">
          <button onClick={() => navigate('/profile')} className="back-button">←</button>
          <h1>Help & Feedback</h1>
        </header>
        <div className="loading-message">Loading...</div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="my-feedback-screen">
      <header className="my-feedback-header">
        <button onClick={() => navigate('/profile')} className="back-button">←</button>
        <h1>Help & Feedback</h1>
      </header>

      <div className="feedback-tabs">
        <button
          className={`tab-button ${activeTab === 'submit' ? 'active' : ''}`}
          onClick={() => setActiveTab('submit')}
        >
          Submit Feedback
        </button>
        <button
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          My Submissions ({feedbackList.length})
        </button>
      </div>

      <main className="my-feedback-content">
        {error && <div className="error-message">{error}</div>}

        {activeTab === 'submit' ? (
          <div className="submit-tab">
            <p className="feedback-instruction">
              We value your input! Please share any feedback, suggestions, or issues you've encountered.
              Your insights help us make the Lightway App better for everyone.
            </p>
            <form onSubmit={handleSubmit} className="feedback-form">
              <div className="form-group">
                <label htmlFor="category">Category:</label>
                <select
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="feedback-select"
                >
                  <option value="General Feedback">General Feedback</option>
                  <option value="Bug Report">Bug Report</option>
                  <option value="Suggestion">Suggestion</option>
                  <option value="Praise">Praise</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="feedbackText">Your Feedback:</label>
                <textarea
                  id="feedbackText"
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Tell us what's on your mind..."
                  rows="6"
                  className="feedback-textarea"
                ></textarea>
              </div>
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
            {submitMessage && (
              <p className={`submit-message ${submitMessage.includes('success') ? 'success' : 'error'}`}>
                {submitMessage}
              </p>
            )}
          </div>
        ) : (
          <div className="history-tab">
            {feedbackList.length === 0 ? (
              <div className="no-feedback">
                <div className="no-feedback-icon">💬</div>
                <h2>No feedback submitted yet</h2>
                <p>When you submit feedback, you'll be able to track its status here.</p>
                <button onClick={() => setActiveTab('submit')} className="submit-feedback-button">
                  Submit Feedback
                </button>
              </div>
            ) : (
              <>
                <p className="feedback-count">
                  You have submitted {feedbackList.length} feedback {feedbackList.length === 1 ? 'item' : 'items'}
                </p>
                <div className="feedback-list">
                  {feedbackList.map((feedback) => (
                    <div key={feedback.id} className="feedback-item">
                      <div className="feedback-item-header">
                        <span className="feedback-category">{feedback.category}</span>
                        <span
                          className="feedback-status"
                          style={{
                            backgroundColor: getStatusColor(feedback.status),
                            color: 'white'
                          }}
                        >
                          {getStatusLabel(feedback.status)}
                        </span>
                      </div>
                      <div className="feedback-item-text">{feedback.feedbackText}</div>
                      <div className="feedback-item-footer">
                        <span className="feedback-date">
                          Submitted {formatDate(feedback.timestamp)}
                        </span>
                        {feedback.resolvedDate && (
                          <span className="feedback-resolved-date">
                            Resolved {formatDate(feedback.resolvedDate)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
};

export default MyFeedbackScreen;
